import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getAllBlogs } from "../api/blogs";

const ACCENT_COLORS = ["#06B6D4", "#8B5CF6", "#10B981", "#F59E0B", "#F43F5E"];

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState("All");

  useEffect(() => {
    let isMounted = true;

    getAllBlogs()
      .then((res) => {
        if (isMounted) setBlogs(res.data);
      })
      .catch(() => {
        if (isMounted) setError("Couldn't load blog posts. Please try again shortly.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const tags = useMemo(() => {
    const allTags = new Set();
    blogs.forEach((blog) => (blog.tags || []).forEach((tag) => allTags.add(tag)));
    return ["All", ...Array.from(allTags)];
  }, [blogs]);

  const featured = useMemo(
    () => blogs.find((blog) => blog.featured) || blogs[0],
    [blogs]
  );

  const filteredBlogs = useMemo(() => {
    return blogs
      .filter((blog) => blog.id !== featured?.id)
      .filter((blog) =>
        activeTag === "All" ? true : (blog.tags || []).includes(activeTag)
      )
      .filter((blog) => {
        const haystack = `${blog.title} ${blog.summary}`.toLowerCase();
        return haystack.includes(search.toLowerCase());
      });
  }, [blogs, featured, activeTag, search]);

  return (
    <section className="section">
      <div className="container">
        <h1 className="section-title">Blog</h1>

        {/* Search + filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "2.5rem", alignItems: "center" }}>
          <div className="search-wrapper" style={{ flex: "1 1 260px" }}>
            <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
            <input
              type="text"
              placeholder="Search articles…"
              className="form-input search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {tags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`tag-filter${activeTag === tag ? " active" : ""}`}
                onClick={() => setActiveTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {loading && <p style={{ color: "var(--text-muted)" }}>Loading articles…</p>}
        {error && <p style={{ color: "var(--rose)" }}>{error}</p>}

        {!loading && !error && (
          <>
            {/* Featured post */}
            {featured && search === "" && activeTag === "All" && (
              <Link to={`/blogs/${featured.id}`} className="featured-card" style={{ marginBottom: "2.5rem" }}>
                <div style={{ background: "linear-gradient(135deg,#06B6D4,#8B5CF6)", minHeight: 220 }} />
                <div style={{ padding: "2rem", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <span className="badge badge-cyan" style={{ width: "fit-content", marginBottom: "0.75rem" }}>
                    Featured
                  </span>
                  <h2 style={{ color: "var(--text-primary)", fontSize: "1.5rem", marginBottom: "0.75rem" }}>
                    {featured.title}
                  </h2>
                  <p style={{ color: "var(--text-muted)", marginBottom: "1rem" }}>{featured.summary}</p>
                  <span style={{ color: "var(--text-dim)", fontSize: "0.8125rem" }}>
                    {featured.date} · {featured.readTime}
                  </span>
                </div>
              </Link>
            )}

            {/* Grid */}
            {filteredBlogs.length === 0 ? (
              <p style={{ color: "var(--text-muted)" }}>No articles match your search.</p>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: "1.5rem",
                }}
              >
                {filteredBlogs.map((blog, index) => (
                  <Link key={blog.id} to={`/blogs/${blog.id}`} className="blog-grid-card">
                    <div
                      className="blog-accent-bar"
                      style={{ backgroundColor: ACCENT_COLORS[index % ACCENT_COLORS.length] }}
                    />
                    <div style={{ padding: "1.5rem", flex: 1, display: "flex", flexDirection: "column" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.75rem" }}>
                        {(blog.tags || []).map((tag) => (
                          <span key={tag} className="tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 style={{ color: "var(--text-primary)", fontSize: "1.125rem", marginBottom: "0.5rem" }}>
                        {blog.title}
                      </h3>
                      <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", flex: 1, marginBottom: "1rem" }}>
                        {blog.summary}
                      </p>
                      <span style={{ color: "var(--text-dim)", fontSize: "0.8125rem" }}>
                        {blog.date} · {blog.readTime}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Blogs;
